import React, { Component } from 'react';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import includes from 'lodash/includes';
import reduce from 'lodash/reduce';
import BrandedTracking from '../brandedTracking';
import UI from '../ui';
import Utils from '../utils';
import Data from '../data';
import SendPreviewEmailModal from './SendPreviewEmailModal.component';
import AccountVerificationModal from '../accountVerification/AccountVerificationModal.component';

const { Communication, Session, Logger, UserRole, withLayoutAndModalContexts, Util } = Utils;
const { Capability } = Data;

const {
	WrappedButton,
	WrappedInputTextareaWithLabel,
	WrappedInputTextWithLabel,
	SaveConfirmation,
	Spinner,
} = UI;
const { BrandedTrackingModal } = BrandedTracking;

class Emails extends Component {
	constructor() {
		super();

		Session.setFormStateDirty(false);

		const isEditable = this.canUserEdit(Session.getUserRole());
		const emailInfo = this.buildDefaultEmailInfo();

		this.state = {
			emailInfo,
			isEditable,
			isChanged: false,
			isLoading: false,
			initialEmailInfo: emailInfo,
			loadingMessage: '',
			saved: false,
			validationErrors: {
				SenderName: 0,
			},
		};
	}

	componentDidMount() {
		// WEBAPPS-29097, call getShipNotificationEmail api for user who is not enabled for branded email
		if (this.canUseBrandedEmails() === false) {
			this.getShipNotificationEmail();
		}
	}

	/**
	 * Return default values for the fields. It's used when user hits 'Reset' button.
	 * @returns {object}
	 */
	buildDefaultEmailInfo = () => {
		const { customerDetails = {} } = Session.getAccountInfo();
		const name = customerDetails.address?.name || '';
		const userName = customerDetails.address?.companyName || name;

		return {
			SenderName: userName,
			Subject: '', // (deprecated) add it due to  “Subject” column in the table does not allow for null value.
			PromotionalFooter: '',
		};
	};

	canUserEdit = userType => {
		if (
			userType === UserRole.SingleUser ||
			userType === UserRole.MultiAdmin ||
			userType === UserRole.RegionAdmin ||
			userType === UserRole.CorpAdmin ||
			userType === UserRole.LocationAdmin
		) {
			return true;
		} else {
			return false;
		}
	};

	/**
	 * Render BrandedEmailsView if BrandedEmails app cap is true.
	 * Otherwise, render Tracking Email view
	 * @returns {boolean}
	 */
	canUseBrandedEmails = () => Session.hasCapability(Capability.BrandedEmails);

	getShipNotificationEmail = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const { emailInfo } = this.state;

		this.setState({
			isLoading: true,
			loadingMessage: 'Loading tracking email...',
		});

		const response = await Communication.getShipNotificationEmail();

		if (!Communication.isSuccessful(response)) {
			this.setState({
				isLoading: false,
				loadingMessage: '',
			});

			return showError({ response });
		}

		const emailInfoResponseData = isObject(response.data) ? response.data : {};

		// WEBAPPS-39768, remove the old promotional footers
		const oldPromotionalFooters = [
			'Never go to the Post Office(TM) Print official U.S. postage right from your PC with Stamps.com. Take our no-risk, 4-week trial today at www.stamps.com/4week.',
			'Easily print USPS shipping labels from your PC or Mac. At Endicia, our goal is to simplify shipping so you can focus on your business. Get started at www.endicia.com.',
		];

		// either sets the value returned from the ajax call or uses what is set in state by default
		const emailValues = {
			SenderName: emailInfoResponseData.SenderName || emailInfo.SenderName,
			Subject: '', // (deprecated) add it due to “Subject” column in the table does not allow for null value.
			PromotionalFooter:
				// if promotional footer saved in server is the old promotional footer, set it to an empty string
				(oldPromotionalFooters.includes(emailInfoResponseData.PromotionalFooter)
					? ''
					: emailInfoResponseData.PromotionalFooter) || emailInfo.PromotionalFooter,
		};

		this.setState({
			emailInfo: emailValues,
			initialEmailInfo: emailValues,
			isLoading: false,
			loadingMessage: '',
		});
	};

	onFieldChange = e => {
		const {
			target: { name, value },
		} = e;

		this.setState(prevState => ({
			...prevState,
			emailInfo: {
				...prevState.emailInfo,
				[name]: value,
			},
			saved: false,
			isChanged: true,
		}));

		Session.setFormStateDirty(true);
	};

	// validate the field on blur
	onFieldBlur = e => {
		const { name, value } = e.target;

		this.setState(({ validationErrors }) => ({
			validationErrors: {
				...validationErrors,
				[name]: value.length === 0 ? -1 : 1,
			},
		}));
	};

	isFormValid = () => {
		const { emailInfo, validationErrors } = this.state;
		let isValid = true;

		Object.keys(validationErrors).forEach(name => {
			// validate the data
			validationErrors[name] = emailInfo[name] === '' ? -1 : 1;

			// set flag to false for any invalid data
			if (emailInfo[name] === '') {
				isValid = false;
			}
		});

		this.setState({
			validationErrors: { ...validationErrors },
		});

		return isValid;
	};

	openBrandedSettings = () => {
		const {
			modalContext: { showModal },
		} = this.props;

		showModal({
			component: BrandedTrackingModal,
		});
	};

	openLearningCenterArticle = () => {
		const { brandName } = Session.getBrand();

		Util.openHelpCenter(brandName === 'Stamps.com' ? '21289815567515' : '21290130771227');
	};

	saveEmailInfo = async () => {
		const { emailInfo, initialEmailInfo } = this.state;
		const {
			modalContext: { showError },
		} = this.props;

		Logger.saveActionData('Save Email Info Button Clicked');

		this.setState({
			isLoading: true,
			loadingMessage: 'Updating tracking email...',
		});

		const response = await Communication.setShipNotificationEmail(emailInfo);

		if (Communication.isSuccessful(response)) {
			const logParams = reduce(
				emailInfo,
				(_logParams, value, key) => {
					// per WEBAPPS-25924, a list of the names of each field that was changed
					if (value !== initialEmailInfo[key]) {
						_logParams.fieldsChanged.push(key);
					}

					// per WEBAPPS-25924, all key value pairs for each form field
					_logParams.newValues.push({ [key]: value });

					return _logParams;
				},
				{
					fieldsChanged: [],
					newValues: [],
				}
			);

			Logger.logToServer({
				category: 'Save Changes',
				fieldsChanged: logParams.fieldsChanged,
				newValues: logParams.newValues,
			});

			// this is to update the "saved" state in the case the user hits the reset button
			this.setState({
				initialEmailInfo: emailInfo,
				saved: true,
				isChanged: false,
			});

			Session.setFormStateDirty(false);
		} else {
			showError({ response });
		}

		this.setState({
			isLoading: false,
			loadingMessage: '',
		});
	};

	checkAccountVerification = () => {
		const {
			modalContext: { showModal },
		} = this.props;

		if (this.isFormValid()) {
			showModal({
				component: AccountVerificationModal,
				data: {
					completionCallback: this.saveEmailInfo,
				},
			});
		}
	};

	launchPreviewModal = () => {
		const {
			modalContext: { showModal },
		} = this.props;
		const { emailInfo } = this.state;
		const { customerDetails = {} } = Session.getAccountInfo();
		const email = customerDetails?.email;

		if (this.isFormValid()) {
			showModal({
				component: SendPreviewEmailModal,
				data: {
					emailAddress: email,
					emailInfo,
				},
			});

			Logger.logToServer({
				category: 'ui_interaction',
				action: 'click',
				ui_element: 'send_preview_email',
			});
		}
	};

	resetEmailFields = () => {
		const { emailInfo } = this.state;
		const defaultEmailInfo = this.buildDefaultEmailInfo();

		// per WEBAPPS-25924, improving Emails logging
		Logger.logToServer({
			category: 'Reset Form',
		});

		this.setState(
			{
				emailInfo: defaultEmailInfo,
				saved: false,
				isChanged: !isEqual(emailInfo, defaultEmailInfo),
			},
			() => this.isFormValid()
		);
	};

	// render tracking email view for user who is NOT enabled for Branded Emails
	renderTrackingEmailView = () => {
		const {
			emailInfo: { SenderName, PromotionalFooter },
			validationErrors,
			isChanged,
			isEditable,
			isLoading,
			loadingMessage,
			saved,
		} = this.state;
		const {
			layoutContext: { isMobile },
		} = this.props;

		// `true` if any field is invalid
		const isValidToSave = !includes(validationErrors, -1);
		const brand = Session.getBrand().brandName;

		return (
			<>
				<div className="account-title">
					{`Tracking Email ${isEditable ? '' : ' (Read-Only)'}`}
					{saved && <SaveConfirmation success text="Changes Saved" />}
				</div>
				<p className="intro-text">
					{brand} can automatically send a Tracking Email to your package recipient
					whenever you print a shipping label. You can use this page to edit the content
					of this email.
				</p>
				<div className="input-container">
					<div className="p-col-12">
						<WrappedInputTextWithLabel
							showErrorBelow={isMobile}
							isInputInvalid={validationErrors.SenderName === -1}
							errorMessage="From name is required."
							label="From:"
							uiElementConfig={{
								type: 'text',
								value: SenderName,
								name: 'SenderName',
								onChange: this.onFieldChange,
								onBlur: this.onFieldBlur,
								disabled: !isEditable,
								required: true,
								maxLength: 50,
								dataqa: 'emailsendername',
							}}
						/>
					</div>
					<div className="p-col-12">
						<WrappedInputTextareaWithLabel
							label="Promotional Footer:"
							uiElementConfig={{
								rows: 5,
								cols: 31,
								maxLength: 500,
								value: PromotionalFooter,
								name: 'PromotionalFooter',
								onChange: this.onFieldChange,
								disabled: !isEditable,
								dataqa: 'emailpromotionalfooter',
							}}
						/>
					</div>
				</div>
				<div className="p-col-12">
					<div className="button-container">
						{isEditable && (
							<WrappedButton
								className="email-button"
								buttonType="primary"
								label="Save"
								onClick={this.checkAccountVerification}
								disabled={!isValidToSave || !isChanged}
								dataqa="emails-save"
							/>
						)}
						{/* WEBAPPS-29731, remove the preview button until we get the working preview URL from Auctane  */}
						{/* <div className="email-button">
						<WrappedButton
							buttonType="secondary"
							label="Preview"
							onClick={this.launchPreviewModal}
							dataqa="emails-preview"
						/>
					</div> */}
						{isEditable && (
							<WrappedButton
								className="email-button"
								buttonType="secondary"
								label="Reset"
								onClick={this.resetEmailFields}
								dataqa="emails-reset"
							/>
						)}
					</div>
				</div>
				{isLoading && <Spinner msg={loadingMessage} />}
			</>
		);
	};

	// render this view for user who is enabled for Branded Emails
	renderBrandedEmailsView = () => (
		<>
			<div className="account-title">Introducing Branded Emails</div>
			<p className="intro-text">
				Keep customers informed with professional branded emails to match your branded
				tracking page.
			</p>
			<p className="intro-text">The best part? We've already set these up for you.</p>
			<p className="intro-text">
				To preview or modify, click <strong>Configure</strong>.
			</p>
			<div className="p-grid">
				<div className="p-col-12">
					<WrappedButton
						buttonType="primary"
						label="Configure"
						onClick={this.openBrandedSettings}
						dataqa="emails-configure"
					/>
					<WrappedButton
						buttonType="secondary"
						label="Learn More"
						onClick={this.openLearningCenterArticle}
						dataqa="emails-learn-more"
					/>
				</div>
			</div>
		</>
	);

	render() {
		const canUseBrandedEmails = this.canUseBrandedEmails();

		return (
			<div className="common-emails">
				{canUseBrandedEmails
					? this.renderBrandedEmailsView()
					: this.renderTrackingEmailView()}
			</div>
		);
	}
}

export default withLayoutAndModalContexts(Emails);
